import React, { ReactNode } from 'react';
import Card from '../ui/Card';

interface TabContainerProps {
  children: ReactNode;
  tabContent?: ReactNode;
  filterContent?: ReactNode;
  tabs?: { id: string; label: string }[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
}

/**
 * TabContainer - A consistent container for tab-based pages
 * Provides standardized layout for pages with tabs
 * - tabContent: The tab navigation component (legacy)
 * - tabs, activeTab, onTabChange: Tab configuration for direct tab support
 * - filterContent: Optional filter controls that appear below tabs
 * - children: The main content area
 */
const TabContainer: React.FC<TabContainerProps> = ({ 
  children, 
  tabContent, 
  filterContent,
  tabs,
  activeTab,
  onTabChange
}) => {
  return (
    <Card className="w-full">
      <div className="p-3">
        {/* Tab navigation */}
        <div className="border-b border-gray-200 pb-2">
          {tabContent ? (
            tabContent
          ) : tabs && activeTab && onTabChange ? (
            <div className="flex space-x-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`px-3 py-2 text-sm font-medium ${activeTab === tab.id
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  onClick={() => onTabChange(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        
        {/* Filters section if provided */}
        {filterContent && (
          <div className="py-3 border-b border-gray-200">
            {filterContent}
          </div>
        )}
        
        {/* Main content area with consistent padding */}
        <div className="pt-3">
          {children}
        </div>
      </div>
    </Card>
  );
};

export default TabContainer;
